import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ReceiverTypes,
  getContactDisplayValue,
  getDefaultBillingContactId,
  getDefaultDeliveryMethod,
  getDefaultElectronicOperator,
  getEmptyBillingAccount,
  getReceiverType,
} from '../../common/utils/billingAccountUtils.js';
import { BillingAccount, BillingAccountDeliveryMethod, ContactType } from '../../generated/api/models.js';
import { BillingAccountDeliveryMethodForm } from './components/BillingAccountDeliveryMethodForm.js';
import { BillingAccountForm } from './components/BillingAccountForm.js';
import type { BillingAccountOrErrorSupplier, CommonError } from '../../common/types/errors.js';
import type {
  Address,
  BillChannel,
  BillingAccountSearchResponse,
  BillingAccountStatus,
  CompanyInfoResponse,
  Contact,
  ContactHeader,
} from '../../generated/api/models.js';

import '../BillingAccountDetails/BillingAccountDetails.scss';

export const getBillingAccountToSave = (
  values: BillingAccountCreateFormValues | BillingAccount | undefined,
  contacts?: Contact[] | ContactHeader[]
) => {
  const newContact = contacts?.find(contact => contact.contactId === values?.billingContactId);
  const newReceiver = contacts?.find(contact => contact.contactId === values?.billReceiverId);
  const receiverContact = newReceiver as Contact;
  const receiverContactHeader = newReceiver as ContactHeader;
  const newContactDisplayName = getContactDisplayValue(newContact);
  const additionalBillReceiversEmptyRemoved = values?.additionalBillReceiverEmails?.filter(Boolean);
  const receiverType = getReceiverType(values);

  const newReceiverValue =
    receiverType === ReceiverTypes.OTHER
      ? {
          billReceiverType: ContactType.PERSON,
          billReceiverName: getContactDisplayValue(newReceiver),
          billReceiverEmail: receiverContact?.person?.email || receiverContactHeader?.email,
        }
      : {};

  return {
    ...values,
    billingContactType: ContactType.PERSON,
    billingContactName: newContactDisplayName,
    additionalBillReceiverEmails: additionalBillReceiversEmptyRemoved,
    ...newReceiverValue,
  };
};

export interface CreateBillingAccountProps {
  billingAccounts: BillingAccountSearchResponse[];
  billChannels: BillChannel[];
  companyInfo?: CompanyInfoResponse;
  contacts: Contact[] | ContactHeader[];
  commonErrors?: CommonError[];
  getPrepareSaveValuesFn?: (prepareSaveValues: BillingAccountOrErrorSupplier) => void;
  setRefCallback?: (key: string, ref: HTMLElement | null) => void;
}

export interface BillingAccountCreateFormValues {
  billingAccountId?: string;
  billingAccountName?: string;
  billingAccountStatus?: BillingAccountStatus;
  billingAccountExtensionName?: string;
  accountBalance?: number;
  payerName: string;
  payerNameExtension?: string;
  payerBusinessId?: string;
  payerAddress: Address;
  billingContactId?: string;
  billFormatType: BillingAccount.BillFormatTypeEnum;
  billingContactName?: string;
  deliveryMethod: BillingAccountDeliveryMethod;
  billLanguage: string;
  billReceiverType?: ContactType;
  additionalBillReceiverEmails?: string[];
  billElectronicOperator?: string;
  billElectronicAddress?: string;
  billReceiverName?: string;
  billReceiverId?: string;
  customerReference1?: string;
  customerReference2?: string;
  customerReference3?: string;
  customerReference4?: string;
}

export const CreateBillingAccount = ({
  billingAccounts,
  billChannels,
  companyInfo,
  contacts,
  setRefCallback,
  getPrepareSaveValuesFn,
  commonErrors,
}: CreateBillingAccountProps) => {
  const billingAccountHeaders = billingAccounts.map(r => r.result);

  const defaultOperatorItem = getDefaultElectronicOperator(billChannels);
  const defaultElectronicAddress =
    defaultOperatorItem?.active && defaultOperatorItem?.electronicAddress ? defaultOperatorItem.electronicAddress : '';

  const initialFormValues: BillingAccountCreateFormValues = {
    payerName: companyInfo?.companyName ?? '',
    billingContactId: getDefaultBillingContactId(billingAccountHeaders),
    billFormatType: BillingAccount.BillFormatTypeEnum.CONSOLIDATED_INVOICE_WITH_ITEMIZATION,
    billLanguage: 'FI',
    billReceiverType: ContactType.PERSON,
    deliveryMethod: getDefaultDeliveryMethod(billingAccountHeaders) ?? BillingAccountDeliveryMethod.ELECTRONIC,
    billElectronicOperator: defaultOperatorItem?.id,
    billingAccountName: '',
    billElectronicAddress: defaultElectronicAddress,
    billReceiverId: '',
    payerAddress: {
      countryCode: 'FIN',
      line1: companyInfo?.address?.line1 ?? '',
      postOffice: companyInfo?.address?.postOffice ?? '',
      postalCode: companyInfo?.address?.postalCode ?? '',
    },
  };

  const methods = useForm<BillingAccountCreateFormValues>({
    defaultValues: initialFormValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (getPrepareSaveValuesFn) {
      getPrepareSaveValuesFn(async () => {
        let validationErrors: { [s: string]: string } | undefined;
        await methods.handleSubmit(
          () => {},
          fieldErrors => {
            validationErrors = Object.fromEntries(
              Object.entries(fieldErrors)
                .filter(([, error]) => error?.message)
                .map(([key, error]) => [key, String(error!.message)])
            );
          }
        )();
        return {
          obj: getBillingAccountToSave(methods.getValues(), contacts) as BillingAccount,
          validationErrors,
        };
      });
    }
  }, [methods, commonErrors, contacts]); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="of-create-billing-account">
      <div className="of-billing-account-details">
        <FormProvider {...methods}>
          <BillingAccountForm setRefCallback={setRefCallback} contacts={contacts} />
          <BillingAccountDeliveryMethodForm
            billingAccount={getEmptyBillingAccount(companyInfo?.companyName ?? '')}
            billChannels={billChannels}
            contacts={contacts || []}
          />
        </FormProvider>
      </div>
    </div>
  );
};
